<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Table from 'dashboard/components-next/table/Table.vue';
import EmptyState from 'dashboard/components-next/empty-state/EmptyState.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', { page });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page: page } });
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});

// Column definitions for the canonical Table primitive.
const columns = computed(() => [
  {
    key: 'activity',
    label: t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
  },
  {
    key: 'time',
    label: t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    width: '14rem',
  },
  {
    key: 'ip',
    label: t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
    width: '10rem',
  },
]);

// Map the audit-log records into the row shape Table consumes.
// `id` is preserved on each row so rowKey defaults to 'id' just works.
const rows = computed(() =>
  records.value.map(auditLogItem => ({
    id: auditLogItem.id,
    activity: generateLogText(auditLogItem),
    time: messageTimestamp(auditLogItem.created_at, 'MMM dd, yyyy hh:mm a'),
    ip: auditLogItem.remote_address,
  }))
);
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('AUDIT_LOGS.HEADER')"
      :description="$t('AUDIT_LOGS.DESCRIPTION')"
      :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
      feature-name="audit_logs"
    />

    <div class="mt-6 flex-1">
      <Table
        :columns="columns"
        :rows="rows"
        :loading="uiFlags.fetchingList"
        :loading-rows="8"
      >
        <template #empty>
          <EmptyState
            icon="i-lucide-history"
            :title="$t('AUDIT_LOGS.LIST.404')"
            variant="compact"
          />
        </template>
      </Table>
      <TableFooter
        v-if="!uiFlags.fetchingList && records.length"
        :current-page="Number(meta.currentPage)"
        :total-count="meta.totalEntries"
        :page-size="meta.perPage"
        class="border-n-weak border-t !px-0 py-4 mt-2"
        @page-change="onPageChange"
      />
    </div>
  </div>
</template>
